/**
 * Administrador de usuarios y de su alcance.
 *
 * Lo que esta pantalla deja claro, y no por estética: **un usuario de negocio
 * sin alcance no ve nada.** Es lo contrario de lo que uno espera de un
 * formulario vacío, así que se dice en la tarjeta, en la tabla y en el aviso.
 * La alternativa —dejarlo en silencio— produce el peor error posible de este
 * sistema, que es una aseguradora viendo la flota de otra.
 *
 * Los permisos que se muestran vienen del servidor. Esta pantalla los obedece
 * para no ofrecer botones inútiles, pero cada endpoint los vuelve a exigir: una
 * bandera en el navegador no autoriza nada (constitución, Art. 4).
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AdminUserView, GuardiaRoleName, SaveUserRequest } from "@core/contracts/api.ts";
import {
  addUserScope,
  ApiError,
  fetchAdminUsers,
  removeUserScope,
  saveUser,
} from "@web/lib/api-client.ts";
import { Card, Notice } from "@web/components/primitives.tsx";

const ROLE_LABEL: Record<GuardiaRoleName, string> = {
  GUARDIA_SUPER_ADMIN: "Administrador GuardIA",
  GESTOR_GPS: "Gestor de GPS",
  ASEGURADORA: "Compañía de seguros",
  CLIENTE: "Cliente",
  ORG_ADMIN: "Administrador de organización",
  ORG_MEMBER: "Miembro de organización",
};

const ROLE_HELP: Record<GuardiaRoleName, string> = {
  GUARDIA_SUPER_ADMIN: "Acceso total: flota, banco de pruebas de calibración y este administrador.",
  GESTOR_GPS: "Ve y analiza toda la flota. No calibra ni administra usuarios.",
  ASEGURADORA: "Ve los vehículos de las aseguradoras que se le asignen abajo, y sus cohortes.",
  CLIENTE: "Ve solo los vehículos que se le asignen por placa o por documento del titular.",
  ORG_ADMIN: "Rol heredado: su alcance sale del vínculo con WOX.",
  ORG_MEMBER: "Rol heredado: su alcance sale del vínculo con WOX.",
};

/** Roles cuyo alcance NO sale de WOX sino de lo que se declare aquí. */
const NEEDS_SCOPE: GuardiaRoleName[] = ["ASEGURADORA", "CLIENTE"];

const SCOPE_LABEL = {
  CARRIER: "Aseguradora",
  PLATE: "Placa",
  DOCUMENT: "Documento del titular",
} as const;

const EMPTY_FORM: SaveUserRequest = {
  id: null,
  email: "",
  role: "CLIENTE",
  displayName: null,
  gpswoxUserId: null,
  gpswoxStatus: null,
  active: true,
};

export function AdminPanel() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<SaveUserRequest>(EMPTY_FORM);
  const [expanded, setExpanded] = useState<string | null>(null);

  const data = useQuery({ queryKey: ["admin-users"], queryFn: fetchAdminUsers });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["admin-users"] });

  const save = useMutation({
    mutationFn: () => saveUser(form),
    onSuccess: () => {
      setForm(EMPTY_FORM);
      refresh();
    },
  });

  const edit = (user: AdminUserView) => {
    setForm({
      id: user.id,
      email: user.email,
      role: user.role,
      displayName: user.displayName,
      gpswoxUserId: user.gpswoxUserId,
      gpswoxStatus: user.gpswoxStatus,
      active: user.active,
    });
    setExpanded(user.id);
  };

  return (
    <div className="space-y-4">
      <Card
        title={form.id ? "Editar usuario" : "Nuevo usuario"}
        hint="El acceso se abre con un enlace al correo. No hay contraseñas que administrar ni que perder."
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="flex flex-col gap-1 text-xs text-zinc-600 dark:text-zinc-400">
            Correo
            <input
              type="email"
              className="rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
            />
          </label>

          <label className="flex flex-col gap-1 text-xs text-zinc-600 dark:text-zinc-400">
            Nombre
            <input
              type="text"
              className="rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
              value={form.displayName ?? ""}
              onChange={(event) => setForm({ ...form, displayName: event.target.value || null })}
            />
          </label>

          <label className="flex flex-col gap-1 text-xs text-zinc-600 dark:text-zinc-400">
            Tipo de usuario
            <select
              className="rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
              value={form.role}
              onChange={(event) => setForm({ ...form, role: event.target.value as GuardiaRoleName })}
            >
              {(Object.keys(ROLE_LABEL) as GuardiaRoleName[]).map((role) => (
                <option key={role} value={role}>
                  {ROLE_LABEL[role]}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-xs text-zinc-600 dark:text-zinc-400">
            ID de usuario en WOX
            <input
              type="number"
              placeholder="opcional"
              className="rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
              value={form.gpswoxUserId ?? ""}
              onChange={(event) => {
                const value = Number(event.target.value);
                setForm({
                  ...form,
                  gpswoxUserId: Number.isSafeInteger(value) && value > 0 ? value : null,
                  gpswoxStatus: Number.isSafeInteger(value) && value > 0 ? "VERIFIED" : null,
                });
              }}
            />
          </label>
        </div>

        <p className="mt-2 text-xs leading-snug text-zinc-500 dark:text-zinc-400">{ROLE_HELP[form.role]}</p>

        {NEEDS_SCOPE.includes(form.role) ? (
          <p className="mt-1 text-xs leading-snug text-amber-700 dark:text-amber-500">
            Este tipo de usuario no verá <strong>ningún</strong> vehículo hasta que se le asigne alcance
            en la tabla de abajo. Es a propósito: un alcance vacío significa nada, nunca todo.
          </p>
        ) : null}

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-zinc-600 dark:text-zinc-400">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(event) => setForm({ ...form, active: event.target.checked })}
            />
            Activo
          </label>
          <button
            type="button"
            disabled={!form.email || save.isPending}
            onClick={() => save.mutate()}
            className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
          >
            {save.isPending ? "Guardando…" : form.id ? "Guardar cambios" : "Crear usuario"}
          </button>
          {form.id ? (
            <button
              type="button"
              onClick={() => setForm(EMPTY_FORM)}
              className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm dark:border-zinc-700"
            >
              Cancelar
            </button>
          ) : null}
          {save.isError ? (
            <span className="text-xs text-amber-700 dark:text-amber-500">
              {save.error instanceof ApiError ? save.error.message : "No se pudo guardar."}
            </span>
          ) : null}
        </div>
      </Card>

      {data.isError ? (
        <Notice title="No se pudo cargar el administrador">
          {data.error instanceof ApiError ? data.error.message : "Error inesperado."}
        </Notice>
      ) : null}

      <Card title={`Usuarios · ${data.data?.users.length ?? 0}`} hint="Un usuario se desactiva, no se borra: borrarlo se llevaría el rastro de lo que consultó.">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[46rem] text-sm">
            <thead>
              <tr className="border-b border-zinc-200 text-left text-xs text-zinc-500 dark:border-zinc-800 dark:text-zinc-400">
                <th className="py-1.5 pr-2 font-medium">Correo</th>
                <th className="py-1.5 pr-2 font-medium">Tipo</th>
                <th className="py-1.5 pr-2 font-medium">Alcance</th>
                <th className="py-1.5 pr-2 font-medium">WOX</th>
                <th className="py-1.5 pr-2 font-medium">Estado</th>
                <th className="py-1.5 pr-2" />
              </tr>
            </thead>
            <tbody>
              {data.data?.users.map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  carriers={data.data.carriers}
                  expanded={expanded === user.id}
                  onToggle={() => setExpanded(expanded === user.id ? null : user.id)}
                  onEdit={() => edit(user)}
                  onChanged={refresh}
                />
              ))}
              {!data.data?.users.length ? (
                <tr>
                  <td colSpan={6} className="py-4 text-center text-sm text-zinc-500">
                    Todavía no hay usuarios.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function UserRow({
  user,
  carriers,
  expanded,
  onToggle,
  onEdit,
  onChanged,
}: {
  user: AdminUserView;
  carriers: string[];
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onChanged: () => void;
}) {
  const [scopeType, setScopeType] = useState<"CARRIER" | "PLATE" | "DOCUMENT">("CARRIER");
  const [value, setValue] = useState("");

  const add = useMutation({
    mutationFn: () => addUserScope({ userId: user.id, scopeType, value }),
    onSuccess: () => {
      setValue("");
      onChanged();
    },
  });
  const drop = useMutation({
    mutationFn: (scopeId: number) => removeUserScope(scopeId),
    onSuccess: onChanged,
  });

  const needsScope = NEEDS_SCOPE.includes(user.role);
  const blind = needsScope && user.scopes.length === 0;

  return (
    <>
      <tr className="border-b border-zinc-100 dark:border-zinc-800/60">
        <td className="py-1.5 pr-2">
          <span className="font-medium text-zinc-900 dark:text-zinc-100">{user.email}</span>
          {user.displayName ? (
            <span className="ml-1.5 text-xs text-zinc-500">{user.displayName}</span>
          ) : null}
        </td>
        <td className="py-1.5 pr-2 text-xs">{ROLE_LABEL[user.role]}</td>
        <td className="py-1.5 pr-2 text-xs">
          {blind ? (
            <span className="text-amber-700 dark:text-amber-500">sin alcance · no ve nada</span>
          ) : user.scopes.length ? (
            <span className="text-zinc-600 dark:text-zinc-400">{user.scopes.length} regla(s)</span>
          ) : (
            <span className="text-zinc-400">por WOX</span>
          )}
        </td>
        <td className="py-1.5 pr-2 text-xs text-zinc-500">
          {user.gpswoxUserId ? `#${user.gpswoxUserId} · ${user.gpswoxStatus ?? "—"}` : "—"}
        </td>
        <td className="py-1.5 pr-2 text-xs">
          {user.active ? (
            <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200">
              Activo
            </span>
          ) : (
            <span className="text-zinc-400">Desactivado</span>
          )}
        </td>
        <td className="py-1.5 pr-2 text-right text-xs">
          <button type="button" className="underline underline-offset-2" onClick={onEdit}>
            Editar
          </button>
          <button type="button" className="ml-3 underline underline-offset-2" onClick={onToggle}>
            {expanded ? "Cerrar" : "Alcance"}
          </button>
        </td>
      </tr>

      {expanded ? (
        <tr className="border-b border-zinc-100 bg-zinc-50 dark:border-zinc-800/60 dark:bg-zinc-900/40">
          <td colSpan={6} className="px-2 py-3">
            <p className="text-xs text-zinc-600 dark:text-zinc-400">
              {needsScope
                ? "Este usuario ve exactamente lo que se liste aquí, y nada más."
                : "Este rol ya ve por su vínculo con WOX. Lo que se agregue aquí se suma a eso."}
            </p>

            <ul className="mt-2 space-y-1">
              {user.scopes.map((scope) => (
                <li key={scope.id} className="flex items-center gap-2 text-sm">
                  <span className="rounded bg-zinc-200 px-1.5 py-0.5 text-xs dark:bg-zinc-800">
                    {SCOPE_LABEL[scope.scopeType]}
                  </span>
                  <span className="font-medium text-zinc-800 dark:text-zinc-200">{scope.value}</span>
                  <button
                    type="button"
                    className="text-xs text-zinc-500 underline underline-offset-2"
                    onClick={() => drop.mutate(scope.id)}
                  >
                    quitar
                  </button>
                </li>
              ))}
              {!user.scopes.length ? (
                <li className="text-sm text-zinc-500">Sin reglas de alcance.</li>
              ) : null}
            </ul>

            <div className="mt-3 flex flex-wrap items-end gap-2">
              <select
                className="rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                value={scopeType}
                onChange={(event) => setScopeType(event.target.value as typeof scopeType)}
              >
                <option value="CARRIER">Aseguradora</option>
                <option value="PLATE">Placa</option>
                <option value="DOCUMENT">Documento del titular</option>
              </select>

              {scopeType === "CARRIER" && carriers.length ? (
                <select
                  className="rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                  value={value}
                  onChange={(event) => setValue(event.target.value)}
                >
                  <option value="">Selecciona…</option>
                  {carriers.map((carrier) => (
                    <option key={carrier} value={carrier}>
                      {carrier}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  placeholder={scopeType === "PLATE" ? "ABC123" : "1088123456"}
                  className="rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                  value={value}
                  onChange={(event) => setValue(event.target.value)}
                />
              )}

              <button
                type="button"
                disabled={!value.trim() || add.isPending}
                onClick={() => add.mutate()}
                className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
              >
                Agregar
              </button>
            </div>

            {scopeType === "CARRIER" && !carriers.length ? (
              <p className="mt-2 text-xs text-amber-700 dark:text-amber-500">
                No hay aseguradoras en la base de atributos todavía. Hasta que se carguen los atributos
                de los vehículos, ninguna asignación por aseguradora va a alcanzar a nada.
              </p>
            ) : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}
